import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

// Schedule Intelligence: on-demand reads only, scoped by an explicit Management
// Person permission that is always rechecked on the server.
const dateKey = (date) => date.toISOString().slice(0, 10);
const utcCalendar = (key) => new Date(`${key}T00:00:00Z`);
const addDays = (key, days) => dateKey(new Date(utcCalendar(key).getTime() + days * 86400000));
const currentSouthAfricanDate = () => {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone:"Africa/Johannesburg", year:"numeric", month:"2-digit", day:"2-digit" }).formatToParts(new Date());
  const part = (name) => parts.find((p) => p.type === name)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
};
const windowFor = (period, selectedDate) => {
  const today = currentSouthAfricanDate();
  const dayOfWeek = utcCalendar(today).getUTCDay();
  const weekStart = addDays(today, -(dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  if (period === "today") return { from: today, through: today };
  if (period === "week") return { from: weekStart, through: today };
  if (period === "lastWeek") return { from: addDays(weekStart,-7), through: addDays(weekStart,-1) };
  if (period === "month") return { from: `${today.slice(0, 7)}-01`, through: today };
  return { from: selectedDate || today, through: selectedDate || today };
};
const OPTIONS = [
  ["today", "Today"], ["week", "This week"], ["lastWeek", "Last week"],
  ["month", "This month"], ["date", "Select date"],
];

const actualDate = (millis) => Number(millis) > 0
  ? new Date(millis).toLocaleDateString("en-ZA", { timeZone: "Africa/Johannesburg", year: "numeric", month: "short", day: "numeric" })
  : "Unknown date";
const actualTime = (millis) => Number(millis) > 0
  ? new Date(millis).toLocaleTimeString("en-GB", { timeZone: "Africa/Johannesburg", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
  : "—";
const differenceLabel = (minutes, kind = "in") => {
  if (!Number.isFinite(minutes)) return "—";
  if (minutes === 0) return "Exactly on time";
  const value = Math.abs(minutes);
  const hours = Math.floor(value / 60);
  const remaining = value % 60;
  const duration = [hours && `${hours}h`, remaining && `${remaining}m`].filter(Boolean).join(" ") || "0m";
  return `${duration} ${minutes > 0 ? `after scheduled ${kind === "out" ? "end" : "start"}` : `before scheduled ${kind === "out" ? "end" : "start"}`}`;
};

// Download a *labelled copy* of the evidence, leaving the stored original intact.
// The time is sourced from the authorised server response, not the device clock.
async function downloadLabelledPhoto(evidence, siteName) {
  const image = new Image();
  image.src = evidence.src;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error("Unable to prepare the attendance photo."));
  });
  const kind = evidence.kind === "out" ? "out" : "in";
  const actualMs = kind === "out" ? evidence.clockOutMs : evidence.clockInMs;
  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  if (!sourceWidth || !sourceHeight) throw new Error("The attendance photo is invalid.");
  const scale = Math.min(1, 1600 / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(640, Math.round(sourceWidth * scale));
  const imageHeight = Math.round(sourceHeight * width / sourceWidth);
  const footerHeight = 190;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = imageHeight + footerHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Your browser could not prepare this download.");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, imageHeight);
  ctx.drawImage(image, 0, 0, width, imageHeight);
  ctx.fillStyle = "#0e1b30";
  ctx.fillRect(0, imageHeight, width, footerHeight);
  ctx.fillStyle = "#38bdf8";
  ctx.fillRect(0, imageHeight, width, 5);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 25px sans-serif";
  ctx.fillText(`MatrixOS Neo · ${evidence.name}`, 24, imageHeight + 39, width - 48);
  ctx.font = "18px sans-serif";
  ctx.fillStyle = "#cbd5e1";
  ctx.fillText(`${siteName} · ${actualDate(actualMs)}`, 24, imageHeight + 72, width - 48);
  const scheduleText = evidence.schedule
    ? `${evidence.schedule.start}–${evidence.schedule.end} (${evidence.schedule.date})`
    : "No matching published schedule";
  ctx.fillText(`Current published schedule: ${scheduleText}`, 24, imageHeight + 104, width - 48);
  ctx.font = "bold 20px sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(`Actual clock-${kind}: ${actualTime(actualMs)} SAST`, 24, imageHeight + 138, width - 48);
  ctx.font = "16px sans-serif";
  ctx.fillStyle = "#cbd5e1";
  const delta = kind === "out" ? evidence.schedule?.endDifferenceMinutes : evidence.schedule?.differenceMinutes;
  ctx.fillText(evidence.schedule && Number.isFinite(delta) ? `Difference: ${differenceLabel(delta, kind)}` : "Published roster unavailable", 24, imageHeight + 169, width - 48);

  const blob = await new Promise((resolve, reject) => canvas.toBlob(
    (value) => value ? resolve(value) : reject(new Error("Download preparation failed.")), "image/jpeg", 0.9
  ));
  const url = URL.createObjectURL(blob);
  const safeName = String(evidence.name || "staff").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 45);
  const localKey = new Date((actualMs || Date.now()) + 7200000).toISOString().slice(0, 10);
  const link = document.createElement("a");
  link.href = url;
  link.download = `MatrixOS_Neo_Clock${kind === "out" ? "Out" : "In"}_${safeName}_${localKey}.jpg`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default function PhotoEvidenceReview({ orgId, stores = [] }) {
  const [storeId, setStoreId] = useState("");
  const [period, setPeriod] = useState("today");
  const [selectedDate, setSelectedDate] = useState(currentSouthAfricanDate);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewingId, setViewingId] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [countExamined, setCountExamined] = useState(0);
  const requestVersion = useRef(0);
  const photoDialogRef = useRef(null);
  const photoCloseRef = useRef(null);
  const photoTriggerRef = useRef(null);

  const activeStores = useMemo(() => stores.filter((s) => s.status !== "archived" && !s.archived && !s.isArchived), [stores]);
  useEffect(() => {
    if (!activeStores.some((s) => s.id === storeId)) setStoreId(activeStores[0]?.id || "");
  }, [storeId, activeStores]);
  const range = useMemo(() => windowFor(period, selectedDate), [period, selectedDate]);
  useEffect(() => {
    requestVersion.current += 1;
    setRows([]); setCursor(""); setLoaded(false); setSelected(null); setError(""); setCountExamined(0); setDownloadError("");
  }, [orgId, storeId, period, selectedDate]);

  const load = async (more = false) => {
    if (!orgId || !storeId || loading || (more && !cursor)) return;
    const version = requestVersion.current;
    setLoading(true); setError(""); setSelected(null);
    try {
      const response = await httpsCallable(functions, "neoAttendancePhotoEvidence")({
        orgId, storeId, action:"list", from:range.from, through:range.through,
        ...(more && cursor ? { cursor } : {}),
      });
      if (version !== requestVersion.current) return;
      const data = response.data || {};
      setRows((previous) => more ? [...previous, ...(data.rows || [])] : (data.rows || []));
      setCursor(data.nextCursor || "");
      setCountExamined((count) => more ? count + Number(data.examined || 0) : Number(data.examined || 0));
      setLoaded(true);
    } catch (err) {
      if (version === requestVersion.current) setError(err?.message || "Unable to load attendance photographs.");
    } finally { setLoading(false); }
  };

  // Keep the lightbox accessible on keyboards and prevent the underlying
  // Schedule Intelligence page from scrolling while a photo is open.
  useEffect(() => {
    if (!selected || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    photoCloseRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelected(null);
      } else if (event.key === "Tab") {
        const controls = Array.from(photoDialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []);
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || !photoDialogRef.current?.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !photoDialogRef.current?.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      photoTriggerRef.current?.focus();
    };
  }, [selected]);

  const view = async (record, kind = "in") => {
    if (!orgId || !storeId || viewingId) return;
    if (typeof document !== "undefined") photoTriggerRef.current = document.activeElement;
    const version = requestVersion.current;
    setViewingId(`${record.id}-${kind}`); setSelected(null); setError(""); setDownloadError("");
    try {
      const { data } = await httpsCallable(functions, "neoAttendancePhotoEvidence")({ orgId, storeId, action:"photo", shiftId:record.id, kind });
      if (!data?.dataBase64 || !["image/jpeg", "image/png", "image/webp"].includes(data.contentType)) {
        throw new Error("Photo evidence was not returned.");
      }
      if (version === requestVersion.current) setSelected({
        id: record.id,
        name: record.name,
        kind,
        src: `data:${data.contentType};base64,${data.dataBase64}`,
        clockInMs: Number(data.clockInMs) || Number(record.clockInMs) || 0,
        clockOutMs: Number(data.clockOutMs) || Number(record.clockOutMs) || 0,
        schedule: data.schedule || null,
      });
    } catch (err) { if (version === requestVersion.current) setError(err?.message || "Photo could not be retrieved."); }
    finally { setViewingId(""); }
  };

  const download = async () => {
    if (!selected || downloading) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const site = activeStores.find((s) => s.id === storeId);
      await downloadLabelledPhoto(selected, site?.name || site?.storeName || storeId);
    } catch (err) {
      setDownloadError(err?.message || "Could not download the attendance photo.");
    } finally { setDownloading(false); }
  };

  return (
    <section className="rounded-2xl border p-5 md:p-6" style={{ borderColor:"rgba(96,165,250,.20)", background:"linear-gradient(145deg, rgba(15,37,65,.92), rgba(8,20,38,.96))" }}>
      <div className="text-xs font-bold uppercase tracking-[.18em] text-cyan-300">Restricted attendance evidence</div>
      <h2 className="mt-1 text-2xl font-black text-white">Attendance Photos</h2>
      <p className="mt-2 text-sm text-white/60">Owner or explicitly authorised Management Persons only. Photographs are opened individually, and access is checked each time.</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm text-white/70">Store
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-slate-900 p-2 text-white">
            {activeStores.map((s) => <option key={s.id} value={s.id}>{s.name || s.storeName || s.id}</option>)}
          </select>
        </label>
        <label className="text-sm text-white/70">Period
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-slate-900 p-2 text-white">
            {OPTIONS.map(([value,label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {period === "date" ? <label className="text-sm text-white/70">Date
          <input type="date" value={selectedDate} max={currentSouthAfricanDate()} onChange={(e) => setSelectedDate(e.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-slate-900 p-2 text-white" />
        </label> : <div className="self-end text-sm text-white/50">{range.from}{range.through !== range.from ? ` to ${range.through}` : ""}</div>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => load(false)} disabled={!storeId || loading} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{loading ? "Loading…" : "Load attendance photos"}</button>
        {loaded ? <span className="text-xs text-white/50">{rows.length} photo record(s) found · {countExamined} attendance record(s) checked</span> : null}
      </div>
      {error ? <p role="alert" className="mt-3 rounded border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</p> : null}
      {rows.length ? <div className="mt-4 space-y-2">
        {rows.map((record) => <div key={`${storeId}-${record.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.025] p-3">
          <div><div className="font-semibold">{record.name}</div><div className="text-xs text-white/50">Shift started {new Date(record.clockInMs).toLocaleString("en-ZA", { timeZone:"Africa/Johannesburg", dateStyle:"medium", timeStyle:"short" })} · {record.role || "Staff"}</div></div>
          <div className="flex flex-wrap gap-2">
            {record.hasClockInPhoto ? <button type="button" disabled={!!viewingId} onClick={() => view(record, "in")} className="rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{viewingId === `${record.id}-in` ? "Opening…" : "Clock-in photo"}</button> : null}
            {record.hasClockOutPhoto ? <button type="button" disabled={!!viewingId} onClick={() => view(record, "out")} className="rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{viewingId === `${record.id}-out` ? "Opening…" : "Clock-out photo"}</button> : null}
          </div>
        </div>)}
      </div> : loaded && !loading ? <p className="mt-4 text-sm text-white/60">No photographs found in this batch for the selected period.</p> : null}
      {loaded && cursor ? <button type="button" onClick={() => load(true)} disabled={loading} className="mt-4 rounded-lg border border-white/25 px-4 py-2 text-sm text-white disabled:opacity-50">{loading ? "Loading…" : "Load more records"}</button> : null}
      {selected && typeof document !== "undefined" ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-3 sm:p-6"
          role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}
        >
          <div
            ref={photoDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${selected.name} clock-${selected.kind || "in"} evidence`}
            className="w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/20 bg-[#0e1b30] p-4 text-white shadow-2xl sm:p-5"
            style={{ maxHeight:"calc(100dvh - 2rem)" }}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-bold">{selected.name}</div>
                <div className="text-sm text-white/60">Clock-{selected.kind || "in"} photo evidence · {actualDate(selected.kind === "out" ? selected.clockOutMs : selected.clockInMs)}</div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={download}
                  disabled={downloading || !(selected.kind === "out" ? selected.clockOutMs : selected.clockInMs)}
                  className="shrink-0 rounded-lg bg-cyan-700 px-3 py-2 text-sm font-semibold hover:bg-cyan-600 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                >
                  {downloading ? "Preparing…" : "Download photo + times"}
                </button>
                <button
                  ref={photoCloseRef}
                  type="button"
                  onClick={() => setSelected(null)}
                  className="shrink-0 rounded-lg border border-white/25 px-3 py-2 text-sm font-semibold hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-lg border border-white/10 bg-white/[.04] p-3">
                <div className="text-xs text-white/50">Current published schedule</div>
                <div className="mt-1 text-base font-semibold">{selected.schedule ? `${selected.schedule.start}–${selected.schedule.end}` : "Unavailable"}</div>
                {selected.schedule?.date && selected.schedule.date !== new Date(selected.clockInMs + 7200000).toISOString().slice(0, 10) ? <div className="text-xs text-white/50">{selected.schedule.date} (overnight)</div> : null}
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[.04] p-3">
                <div className="text-xs text-white/50">Actual clock-{selected.kind || "in"} · SAST</div>
                <div className="mt-1 text-base font-semibold">{actualTime(selected.kind === "out" ? selected.clockOutMs : selected.clockInMs)}</div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[.04] p-3">
                <div className="text-xs text-white/50">Difference from schedule</div>
                <div className="mt-1 text-sm font-semibold">{selected.schedule && Number.isFinite(selected.kind === "out" ? selected.schedule.endDifferenceMinutes : selected.schedule.differenceMinutes)
                  ? differenceLabel(selected.kind === "out" ? selected.schedule.endDifferenceMinutes : selected.schedule.differenceMinutes, selected.kind)
                  : "No matching published shift"}</div>
              </div>
            </div>
            {downloadError ? <p role="alert" className="mb-3 rounded border border-rose-400/30 bg-rose-500/10 p-2 text-sm text-rose-100">{downloadError}</p> : null}
            <div className="flex justify-center rounded-xl bg-black/40 p-2">
              <img
                src={selected.src}
                alt={`Clock-${selected.kind || "in"} evidence for ${selected.name}`}
                className="h-auto max-w-full rounded-lg object-contain"
                style={{ maxHeight:"min(70vh, 720px)" }}
              />
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </section>
  );
}
